import {
  Box,
  Button,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { VariableSizeList } from "react-window";
import AddIcon from "@mui/icons-material/Add";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { Helmet } from "react-helmet-async";
import CardLayout from "../../layouts/CardLayout";
import { Form, FormikProvider, useFormik } from "formik";
import FieldErrorMessage from "../../components/FieldErrorMessage";
import SaveButton from "../../components/SaveButton";
import { useNavigate, useParams } from "react-router-dom";
import contestmanagerapi from "../../services/contestmanager";
import matchapi from "../../services/matchService";
import { toast } from "react-toastify";
import errorHandler from "../../utils/errorHandler";
import CustomLoader from "../../components/CustomLoader";

import { conestmanagerSchema } from "../../utils/Schema";
import { GridDeleteIcon } from "@mui/x-data-grid";

const LISTBOX_PADDING = 8; // px

function renderRow(props) {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: style.top + LISTBOX_PADDING,
    },
  });
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});

function useResetCache(data) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

const ListboxComponent = React.forwardRef(function ListboxComponent(
  props,
  ref
) {
  const { children, ...other } = props;
  const itemData = React.Children.toArray(children);
  const itemCount = itemData.length;
  const itemSize = 48;

  const getChildSize = () => {
    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={itemData}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const AddEditContestManager = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [matches, setmatches] = useState([]);
  const [matchesFetched, setMatchesFetched] = useState(false);
  const [isLoadingMatches, setIsLoadingMatches] = useState(true);

  const matchMenuItems = useMemo(() => {
    return matches.map((match) => (
      <MenuItem
        key={match.id}
        value={match.id}
        sx={{
          whiteSpace: "normal",
          py: 1.5,
          minHeight: "auto",
        }}
      >
        {match.match_number} - {match.match_type} - {match.status}
      </MenuItem>
    ));
  }, [matches]);

  const formik = useFormik({
    initialValues: {
      match_id: "",
      name: "",
      entry_fee: "",
      start_time: null,
      end_time: null,

      total_spots: "",
      total_prize_pool: "",
      contest_type: "",
      per_user_entry: "",
      prize_breakup: [{ position: "", amount: "" }],
      winnings: [{ from: "", to: "", price: "" }],
    },
    validationSchema: conestmanagerSchema,
    onSubmit: async (values, { setSubmitting, validateForm, setTouched }) => {
      try {
        setTouched({
          ...Object.keys(values).reduce((acc, field) => {
            acc[field] = true;
            return acc;
          }, {}),
        });
        const errors = await validateForm();
        if (Object.keys(errors).length > 0) {
          toast.error("Please fix all validation errors before submitting");
          return;
        }
        if (!values.start_time || !values.end_time) {
          toast.error("Please select both start and end dates");
          return;
        }

        const filteredPrizeBreakup = values.prize_breakup.filter(
          (item) => item.position && item.amount
        );
        const filteredWinnings = values.winnings.filter(
          (item) => item.from && item.to && item.price
        );
        const prizeBreakupObj = filteredPrizeBreakup.reduce((acc, curr) => {
          acc[curr.position] = curr.amount;
          return acc;
        }, {});

        const payload = {
          ...values,
          prize_breakup: prizeBreakupObj,
          winnings: filteredWinnings,
          start_time: values.start_time.toISOString(),
          end_time: values.end_time.toISOString(),
        };

        console.log("Submitting payload:", payload);
        const res = id
          ? await contestmanagerapi.edit(payload)
          : await contestmanagerapi.add(payload);

        toast.success(res?.data?.message);
        navigate("/contest");
      } catch (error) {
        errorHandler(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    getFieldProps,
    setFieldValue,
    resetForm,
    handleChange,
  } = formik;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const response = await contestmanagerapi.getById({ id });
          const resData = response?.data?.data;
          const prizeBreakupArray = resData?.rules
            ? Object.entries(resData.rules).map(([position, amount]) => ({
                position,
                amount,
              }))
            : [{ position: "", amount: "" }];

          resetForm({
            values: {
              id: resData?.id,
              match_id: resData?.match_id || "",
              name: resData?.name || "",

              entry_fee: resData?.entry_fee
                ? Math.floor(resData.entry_fee)
                : "",
              start_time: resData?.start_time
                ? new Date(resData.start_time)
                : null,
              end_time: resData?.end_time ? new Date(resData.end_time) : null,
              total_spots: resData?.total_spots || "",
              total_prize_pool: resData?.prize_pool || "",
              contest_type: resData?.contest_type || "",
              per_user_entry: resData?.per_user_entry || "",
              prize_breakup: prizeBreakupArray,
              winnings: resData?.winnings || [{ from: "", to: "", price: "" }],
            },
          });
          setData(resData);
        } else {
          resetForm({
            values: {
              match_id: "",
              name: "",
              // status:"",
              entry_fee: "",
              start_time: null,
              end_time: null,
              total_spots: "",
              total_prize_pool: "",
              contest_type: "",
              per_user_entry: "",
              prize_breakup: [{ position: "", amount: "" }],
              winnings: [{ from: "", to: "", price: "" }],
            },
          });
          setData(null);
        }
      } catch (error) {
        errorHandler(error);
      }
    };

    fetchData();
  }, [id, resetForm]);

  useEffect(() => {
    if (touched.name && errors.name) {
      setFieldValue("name", "");
    }
  }, [touched.name, errors.name]);

  const fetchMatches = async () => {
    try {
      setIsLoadingMatches(true);

      const res = await matchapi.getAll();
      setmatches(res?.data?.data?.result || []);
      setMatchesFetched(true);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoadingMatches(false);
    }
  };

  useEffect(() => {
    if (!matchesFetched) {
      fetchMatches();
    }
  }, []);

  return (
    <div>
      <Box>
        <Helmet>
          <title>{id ? "Edit " : "Add "}Contest </title>
        </Helmet>
        <CardLayout>
          <CardHeader title={`${id ? "Edit" : "Add"} Contest `} />
          <CardContent>
            {isLoadingMatches ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="200px"
                width="100%"
              >
                <CustomLoader />
              </Box>
            ) : (
              <FormikProvider value={formik}>
                <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={6}>
                      <FormControl fullWidth>
                        <Select
                          MenuProps={{
                            PaperProps: {
                              style: {
                                maxHeight: 300,
                                "&::-webkit-scrollbar": {
                                  width: "0.4em",
                                },
                                "&::-webkit-scrollbar-track": {
                                  boxShadow: "inset 0 0 6px rgba(0,0,0,0.00)",
                                  webkitBoxShadow:
                                    "inset 0 0 6px rgba(0,0,0,0.00)",
                                },
                                "&::-webkit-scrollbar-thumb": {
                                  backgroundColor: "rgba(0,0,0,.1)",
                                  outline: "1px solid slategrey",
                                  borderRadius: "4px",
                                },
                              },
                            },
                            MenuListProps: {
                              style: {
                                paddingTop: 0,
                                paddingBottom: 0,
                              },
                            },
                          }}
                          displayEmpty
                          required
                          name="match_id"
                          value={values.match_id}
                          onChange={handleChange}
                          error={Boolean(touched.match_id && errors.match_id)}
                        >
                          <MenuItem disabled value="">
                            <span>Select Match</span>
                          </MenuItem>
                          {matchMenuItems}
                        </Select>
                        {touched.match_id && errors.match_id && (
                          <FormHelperText error>
                            {errors.match_id}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        required
                        value={values.name}
                        onChange={(e) => {
                          const filteredValue = e.target.value.replace(
                            /[^a-zA-Z\s]/g,
                            ""
                          );
                          setFieldValue("name", filteredValue);
                        }}
                        onBlur={(e) => {
                          const trimmedValue = e.target.value.trim();
                          if (trimmedValue !== e.target.value) {
                            setFieldValue("name", trimmedValue);
                          }
                          formik.setFieldTouched("name", true, true);
                        }}
                        error={Boolean(touched?.name && errors?.name)}

                        // inputProps={{
                        //   pattern: "[a-zA-Z\\s]*",
                        // }}
                      />
                      <FieldErrorMessage formik={formik} name="name" />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Entry Fee"
                        required
                        type="number"
                        error={Boolean(touched?.entry_fee && errors?.entry_fee)}
                        {...getFieldProps("entry_fee")}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <FieldErrorMessage formik={formik} name="entry_fee" />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Total Spots"
                        required
                        type="number"
                        error={Boolean(
                          touched?.total_spots && errors?.total_spots
                        )}
                        {...getFieldProps("total_spots")}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <FieldErrorMessage formik={formik} name="total_spots" />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Total Prize Pool"
                        required
                        type="number"
                        error={Boolean(
                          touched?.total_prize_pool && errors?.total_prize_pool
                        )}
                        {...getFieldProps("total_prize_pool")}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <FieldErrorMessage
                        formik={formik}
                        name="total_prize_pool"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <TextField
                        fullWidth
                        label="Per User Entry"
                        required
                        type="number"
                        error={Boolean(
                          touched?.per_user_entry && errors?.per_user_entry
                        )}
                        {...getFieldProps("per_user_entry")}
                        onKeyDown={(e) => {
                          if (["e", "E", "+", "-"].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                      />
                      <FieldErrorMessage
                        formik={formik}
                        name="per_user_entry"
                      />
                    </Grid>

                    <Grid item xs={6}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <Grid container spacing={2}>
                          <Grid item xs={6} md={6}>
                            <DatePicker
                              inputFormat="MM/dd/yyyy"
                              label="Start Date"
                              value={values.start_time}
                              onChange={(newValue) => {
                                // Prevent past dates
                                if (
                                  newValue &&
                                  newValue < new Date().setHours(0, 0, 0, 0)
                                ) {
                                  toast.error(
                                    "Start date cannot be in the past"
                                  );
                                  return;
                                }

                                // If end date exists and is before new start date, clear it
                                if (
                                  values.end_time &&
                                  newValue > values.end_time
                                ) {
                                  setFieldValue("end_time", null);
                                }

                                setFieldValue("start_time", newValue);
                              }}
                              minDate={new Date()}
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  {...params}
                                  error={Boolean(
                                    touched.start_time && errors.start_time
                                  )}
                                  helperText={
                                    touched.start_time && errors.start_time
                                  }
                                />
                              )}
                            />
                          </Grid>

                          <Grid item xs={6} md={6}>
                            <DatePicker
                              inputFormat="MM/dd/yyyy"
                              label="End Date"
                              value={values.end_time}
                              onChange={(newValue) => {
                                // Prevent past dates
                                if (
                                  newValue &&
                                  newValue < new Date().setHours(0, 0, 0, 0)
                                ) {
                                  toast.error("End date cannot be in the past");
                                  return;
                                }

                                // Prevent dates before start date
                                if (
                                  values.start_time &&
                                  newValue < values.start_time
                                ) {
                                  toast.error(
                                    "End date must be after start date"
                                  );
                                  return;
                                }

                                setFieldValue("end_time", newValue);
                              }}
                              minDate={values.start_time || new Date()}
                              disabled={!values.start_time} // Disable until start date is selected
                              renderInput={(params) => (
                                <TextField
                                  fullWidth
                                  {...params}
                                  error={Boolean(
                                    touched.end_time && errors.end_time
                                  )}
                                  helperText={
                                    touched.end_time && errors.end_time
                                  }
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      </LocalizationProvider>
                    </Grid>

                    <Grid item xs={6}>
                      <FormControl
                        fullWidth
                        error={Boolean(
                          formik.touched.contest_type &&
                            formik.errors.contest_type
                        )}
                      >
                        <InputLabel id="contesttype-label">
                          Contest Type
                        </InputLabel>
                        <Select
                          labelId="contesttype-label"
                          id="contest_type"
                          name="contest_type"
                          required
                          value={formik.values.contest_type}
                          onChange={(e) =>
                            formik.setFieldValue("contest_type", e.target.value)
                          }
                          displayEmpty
                          label="Contest Type"
                          InputLabelProps={{ shrink: true }}
                        >
                          <MenuItem value="single">Single</MenuItem>
                          <MenuItem value="multiple">Multiple</MenuItem>
                        </Select>
                        {formik.touched.contest_type &&
                          formik.errors.contest_type && (
                            <FormHelperText>
                              {formik.errors.contest_type}
                            </FormHelperText>
                          )}
                      </FormControl>
                    </Grid>

                    {formik.touched.contest_type &&
                      formik.errors.contest_type && (
                        <FormHelperText error>
                          {formik.errors.contest_type}
                        </FormHelperText>
                      )}

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Prize Breakup
                      </Typography>
                      <Grid container spacing={2}>
                        {values.prize_breakup.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item xs={5}>
                              <TextField
                                fullWidth
                                required
                                label="Position"
                                name={`prize_breakup[${index}].position`}
                                value={item.position}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                error={Boolean(
                                  touched.prize_breakup?.[index]?.position &&
                                    errors.prize_breakup?.[index]?.position
                                )}
                                helperText={
                                  touched.prize_breakup?.[index]?.position &&
                                  errors.prize_breakup?.[index]?.position
                                }
                              />
                            </Grid>
                            <Grid item xs={5}>
                              <TextField
                                fullWidth
                                required
                                label="Amount"
                                name={`prize_breakup[${index}].amount`}
                                type="number"
                                value={item.amount}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                error={Boolean(
                                  touched.prize_breakup?.[index]?.amount &&
                                    errors.prize_breakup?.[index]?.amount
                                )}
                                helperText={
                                  touched.prize_breakup?.[index]?.amount &&
                                  errors.prize_breakup?.[index]?.amount
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              display="flex"
                              alignItems="center"
                            >
                              <Box>
                                {!(
                                  values.prize_breakup.length === 1 &&
                                  values.prize_breakup[0].position === "" &&
                                  values.prize_breakup[0].amount === ""
                                ) && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                      const newPrizeBreakup = [
                                        ...values.prize_breakup,
                                      ];
                                      newPrizeBreakup.splice(index, 1);
                                      setFieldValue(
                                        "prize_breakup",
                                        newPrizeBreakup
                                      );
                                    }}
                                    startIcon={<GridDeleteIcon />}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </Box>
                            </Grid>
                          </React.Fragment>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setFieldValue("prize_breakup", [
                                ...values.prize_breakup,
                                { position: "", amount: "" },
                              ]);
                            }}
                            startIcon={<AddIcon />}
                            sx={{ mt: 2 }}
                          >
                            Add Prize Breakup
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Winnings
                      </Typography>
                      <Grid container spacing={2}>
                        {values.winnings.map((item, index) => (
                          <React.Fragment key={index}>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                required
                                label="From"
                                name={`winnings[${index}].from`}
                                type="number"
                                value={item.from}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key))
                                    e.preventDefault();
                                }}
                                error={Boolean(
                                  touched?.winnings?.[index]?.from &&
                                    errors?.winnings?.[index]?.from
                                )}
                                helperText={
                                  touched?.winnings?.[index]?.from &&
                                  errors?.winnings?.[index]?.from
                                }
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                required
                                label="To"
                                name={`winnings[${index}].to`}
                                type="number"
                                value={item.to}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key))
                                    e.preventDefault();
                                }}
                                error={Boolean(
                                  touched?.winnings?.[index]?.to &&
                                    errors?.winnings?.[index]?.to
                                )}
                                helperText={
                                  touched?.winnings?.[index]?.to &&
                                  errors?.winnings?.[index]?.to
                                }
                              />
                            </Grid>
                            <Grid item xs={3}>
                              <TextField
                                fullWidth
                                required
                                label="Price"
                                name={`winnings[${index}].price`}
                                type="number"
                                value={item.price}
                                onChange={handleChange}
                                onKeyDown={(e) => {
                                  if (["e", "E", "+", "-"].includes(e.key))
                                    e.preventDefault();
                                }}
                                error={Boolean(
                                  touched?.winnings?.[index]?.price &&
                                    errors?.winnings?.[index]?.price
                                )}
                                helperText={
                                  touched?.winnings?.[index]?.price &&
                                  errors?.winnings?.[index]?.price
                                }
                              />
                            </Grid>
                            <Grid
                              item
                              xs={2}
                              display="flex"
                              alignItems="center"
                            >
                              <Box>
                                {!(
                                  values.winnings.length === 1 &&
                                  values.winnings[0].from === "" &&
                                  values.winnings[0].to === "" &&
                                  values.winnings[0].price === ""
                                ) && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    onClick={() => {
                                      const newWinnings = [...values.winnings];
                                      newWinnings.splice(index, 1);
                                      setFieldValue("winnings", newWinnings);
                                    }}
                                    startIcon={<GridDeleteIcon />}
                                  >
                                    Delete
                                  </Button>
                                )}
                              </Box>
                            </Grid>
                          </React.Fragment>
                        ))}
                        <Grid item xs={12}>
                          <Button
                            type="button"
                            variant="contained"
                            color="primary"
                            onClick={() => {
                              setFieldValue("winnings", [
                                ...values.winnings,
                                { from: "", to: "", price: "" },
                              ]);
                            }}
                            startIcon={<AddIcon />}
                            sx={{ mt: 2 }}
                          >
                            Add More
                          </Button>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>

                  <SaveButton
                    loading={formik.isSubmitting}
                    id={id}
                    disabled={!formik.isValid || formik.isSubmitting}
                  />
                </Form>
              </FormikProvider>
            )}
          </CardContent>
        </CardLayout>
      </Box>
    </div>
  );
};

export default AddEditContestManager;
